var _dec, _dec2, _class, _ColorMixer;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { withStyle } from '@instructure/emotion';
import { omitProps } from '@instructure/ui-react-utils';
import { testable } from '@instructure/ui-testable';
import { isValid } from '@instructure/ui-color-utils';
import conversions from '@instructure/ui-color-utils';
import { logWarn as warn } from '@instructure/console';
import ColorPalette from './ColorPalette';
import Slider from './Slider';
import RGBAInput from './RGBAInput';
import { propTypes, allowedProps } from './props';
import generateStyle from './styles';

/**
---
category: components
---
**/
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
let ColorMixer = (_dec = withStyle(generateStyle, null), _dec2 = testable(), _dec(_class = _dec2(_class = (_ColorMixer = class ColorMixer extends Component {
  constructor(props) {
    super(props);
    this.ref = null;
    this._width = 272;
    this._paletteHeight = 160;
    this._sliderHeight = 8;
    this._sliderIndicatorRadius = 6;
    this._paletteIndicatorRadius = 6;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
    this.state = {
      h: 0,
      s: 0,
      v: 0,
      a: 1
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props);
    warn(isValid(this.props.value), `[ColorMixer] The passed color value is not valid.`);
    this.setState({
      ...conversions.colorToHsva(this.props.value)
    });
  }
  componentDidUpdate(prevProps, prevState) {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2);
    const _this$state = this.state,
      h = _this$state.h,
      s = _this$state.s,
      v = _this$state.v,
      a = _this$state.a;
    if (prevState.h !== h || prevState.s !== s || prevState.v !== v || prevState.a !== a) {
      this.props.onChange(conversions.colorToHex8({
        h,
        s,
        v,
        a
      }));
    }
    if (prevProps.value !== this.props.value && conversions.colorToHex8({
      h,
      s,
      v,
      a
    }) !== this.props.value) {
      this.setState({
        ...conversions.colorToHsva(this.props.value)
      });
    }
  }
  render() {
    const _this$props3 = this.props,
      disabled = _this$props3.disabled,
      styles = _this$props3.styles,
      withAlpha = _this$props3.withAlpha,
      rgbRedInputScreenReaderLabel = _this$props3.rgbRedInputScreenReaderLabel,
      rgbGreenInputScreenReaderLabel = _this$props3.rgbGreenInputScreenReaderLabel,
      rgbBlueInputScreenReaderLabel = _this$props3.rgbBlueInputScreenReaderLabel,
      rgbAlphaInputScreenReaderLabel = _this$props3.rgbAlphaInputScreenReaderLabel,
      colorSliderNavigationExplanationScreenReaderLabel = _this$props3.colorSliderNavigationExplanationScreenReaderLabel,
      alphaSliderNavigationExplanationScreenReaderLabel = _this$props3.alphaSliderNavigationExplanationScreenReaderLabel,
      colorPaletteNavigationExplanationScreenReaderLabel = _this$props3.colorPaletteNavigationExplanationScreenReaderLabel;
    const _this$state2 = this.state,
      h = _this$state2.h,
      s = _this$state2.s,
      v = _this$state2.v,
      a = _this$state2.a;
    return _jsxs("div", {
      ...omitProps(this.props, ColorMixer.allowedProps),
      "aria-disabled": disabled,
      ref: this.handleRef,
      css: styles === null || styles === void 0 ? void 0 : styles.colorMixer,
      children: [_jsxs("span", {
        css: styles === null || styles === void 0 ? void 0 : styles.sliderAndPaletteContainer,
        "aria-label": `${conversions.colorToHex8({
          h,
          s,
          v,
          a
        })}`,
        "aria-live": "polite",
        children: [_jsx(ColorPalette, {
          disabled: disabled,
          width: this._width,
          height: this._paletteHeight,
          indicatorRadius: this._paletteIndicatorRadius,
          hue: h,
          color: {
            h,
            s,
            v
          },
          onChange: color => {
            this.setState({
              s: color.s,
              v: color.v
            });
          },
          navigationExplanationScreenReaderLabel: colorPaletteNavigationExplanationScreenReaderLabel
        }), _jsx(Slider, {
          disabled: disabled,
          isColorSlider: true,
          width: this._width,
          height: this._sliderHeight,
          indicatorRadius: this._sliderIndicatorRadius,
          value: h,
          minValue: 0,
          maxValue: 359,
          color: conversions.colorToHex8({
            h,
            s,
            v,
            a
          }),
          onChange: hue => {
            this.setState({
              h: hue
            });
          },
          navigationExplanationScreenReaderLabel: colorSliderNavigationExplanationScreenReaderLabel
        }), withAlpha && _jsx(Slider, {
          disabled: disabled,
          width: this._width,
          height: this._sliderHeight,
          indicatorRadius: this._sliderIndicatorRadius,
          color: conversions.colorToHex8({
            h,
            s,
            v
          }),
          value: a,
          minValue: 0,
          maxValue: 1,
          onChange: opacity => this.setState({
            a: opacity / 100
          }),
          navigationExplanationScreenReaderLabel: alphaSliderNavigationExplanationScreenReaderLabel
        })]
      }), _jsx(RGBAInput, {
        disabled: disabled,
        label: withAlpha ? 'RGBA' : 'RGB',
        width: this._width,
        value: conversions.colorToRGB({
          h,
          s,
          v,
          a
        }),
        onChange: color => this.setState({
          ...conversions.colorToHsva(color)
        }),
        withAlpha: withAlpha,
        rgbRedInputScreenReaderLabel: rgbRedInputScreenReaderLabel,
        rgbGreenInputScreenReaderLabel: rgbGreenInputScreenReaderLabel,
        rgbBlueInputScreenReaderLabel: rgbBlueInputScreenReaderLabel,
        rgbAlphaInputScreenReaderLabel: rgbAlphaInputScreenReaderLabel
      })]
    });
  }
}, _ColorMixer.displayName = "ColorMixer", _ColorMixer.propTypes = propTypes, _ColorMixer.allowedProps = allowedProps, _ColorMixer.componentId = 'ColorMixer', _ColorMixer.defaultProps = {
  value: '#000',
  withAlpha: false,
  disabled: false
}, _ColorMixer)) || _class) || _class);
export { ColorMixer };
export default ColorMixer;